import { useState, useCallback, useRef } from "react";
import axios from "axios";
import { IBGEState, IBGECity, CitySearchResult } from "./types";

const IBGE_BASE_URL = "https://servicodados.ibge.gov.br/api/v1/localidades";

export const useCitySearch = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<CitySearchResult[]>([]);
  const [allCities, setAllCities] = useState<CitySearchResult[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadAllCities = useCallback(async (): Promise<void> => {
    if (allCities.length > 0) return;

    try {
      const statesResponse = await axios.get<IBGEState[]>(
        `${IBGE_BASE_URL}/estados`
      );
      const states = statesResponse.data;

      const allCitiesPromises = states.map((state) =>
        axios.get<IBGECity[]>(`${IBGE_BASE_URL}/estados/${state.id}/municipios`)
      );

      const allCitiesResponses = await Promise.all(allCitiesPromises);

      const citiesData: CitySearchResult[] = [];

      allCitiesResponses.forEach((response, index) => {
        const cities = response.data;
        const state = states[index];

        cities.forEach((city) => {
          citiesData.push({
            city: city.nome,
            state: state.nome,
            stateAbbr: state.sigla,
            fullName: `${city.nome} - ${state.sigla}`,
          });
        });
      });

      setAllCities(citiesData);
    } catch (error) {
      console.error("Erro ao carregar cidades:", error);
    }
  }, [allCities.length]);

  const searchCities = useCallback(
    async (query: string): Promise<void> => {
      if (!query || query.trim().length < 2) {
        setResults([]);
        return;
      }

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(async () => {
        setIsLoading(true);

        try {
          if (allCities.length === 0) {
            await loadAllCities();
          }

          const filteredResults = allCities
            .filter(
              (city) =>
                city.city.toLowerCase().includes(query.toLowerCase()) ||
                city.state.toLowerCase().includes(query.toLowerCase())
            )
            .sort((a, b) => a.city.localeCompare(b.city))
            .slice(0, 10);

          setResults(filteredResults);
        } catch (error) {
          console.error("Erro ao buscar cidades:", error);
          setResults([]);
        } finally {
          setIsLoading(false);
        }
      }, 300);
    },
    [allCities, loadAllCities]
  );

  const clearResults = useCallback(() => {
    setResults([]);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  return {
    isLoading,
    results,
    searchCities,
    clearResults,
  };
};
